import React from 'react';

import { styled } from '../theme';
import { CommonComponentProps } from '../types/components';

const SIZES = {
    sm: 160,
    md: 280,
};

export type Size = keyof typeof SIZES;
const sizeGetter = (size: Size = 'md') => SIZES[size] || SIZES.md;

export const RoundShape = 'round';
export const SquareShape = 'square';
export type Shape = typeof RoundShape | typeof SquareShape;

type Props = CommonComponentProps & {
    src: string;
    alt?: string;
    shape?: Shape;
    size?: Size;
    aspectRatio?: number;
    onClick?: () => void;
    style?: React.CSSProperties;
    className?: string;
    getSize?: (size: Size) => number;
};

const Container = styled('div')<Props>(({ onClick }) => ({
    position: 'relative',
    display: 'inline-box',
    cursor: onClick ? 'pointer' : 'auto',
}));

const AspectRatioBox = styled('div')<Props>(
    ({ aspectRatio = 1 / 2, size, getSize = sizeGetter }) => ({
        display: 'block',
        width: size != null ? getSize(size) : '100%',
        paddingTop: aspectRatio * 100 + '%',
    })
);

const StyledImage = styled('div')<Props>(({ theme, shape, src }) => ({
    borderRadius: shape === RoundShape ? '50%' : theme.borderRadius.base,
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat center top',
    backgroundPosition: 'center',
    backgroundImage: `url(${src})`,
}));

const Image: React.FC<Props> = ({
    children,
    size = 'default',
    onClick,
    src,
    alt,
    aspectRatio,
    style,
    shape = SquareShape,
    getSize,
    ...props
}) => {
    return (
        <Container onClick={onClick} style={style} {...props}>
            <AspectRatioBox aspectRatio={aspectRatio} size={size} getSize={getSize} />
            <StyledImage role="img" aria-label={alt} src={src} shape={shape}>
                {children}
            </StyledImage>
        </Container>
    );
};

export default Image;
